import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useCartChanges } from "@/hooks/useAutoRefresh";
import { TRY } from "@/lib/format";
import { toast } from "sonner";

// Canlı + çevrimdışı dayanıklı POS sepeti.
//  • Sepet sunucuda yaşar (pos_carts); her yerel değişiklik bir "op" olarak kuyruğa girer ve ~180 ms içinde toplu gönderilir.
//  • Sunucu yanıtı (güncel sepet) yerel durumu ezer, gönderilmemiş op'lar üstüne yeniden uygulanır → başka kullanıcıların
//    düzenlemeleri anında görünür, kendi yazdığınız asla geri sıçramaz.
//  • Bağlantı yoksa sepet + kuyruk cihazda (localStorage) saklanır; bağlantı gelince otomatik akar.
//  • Ödeme sırasında bağlantı koparsa satış "bekleyen" kuyruğa alınır ve aynı Idempotency anahtarıyla otomatik gönderilir (çift kayıt olmaz).
const LS_CART = "ge_pos_cart_v1";
const LS_PENDING = "ge_pos_pending_sales_v1";
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const fmt = (v) => String(r2(v));
const uuid = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
const readLS = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k)); return v == null ? d : v; } catch { return d; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* depolama dolu */ } };
const isTransient = (e) => !e?.response || [502, 503, 504, 520, 521, 522, 523, 524].includes(e.response.status);
const cartStatusOf = (e) => e?.response?.headers?.["x-cart-status"] || "";

export const EMPTY_CART = { cartId: null, version: 0, status: "open", items: [], mode: "genel", title: "", note: "", payMethod: "nakit", owner: "", ownerId: null, editors: [], updatedBy: "", adminPresent: false };

const modeGross = (m) => (m.unit_gross != null ? r2(m.unit_gross) : r2((m.unit_net || 0) * (1 + (m.kdv_rate || 0) / 100)));
export const applyModeToItems = (items, mode) => items.map((l) => {
  const pm = l.pos && l.pos[mode];
  return pm ? { ...l, unit_net: pm.unit_net, gross: fmt(modeGross(pm)), manual_price: !!pm.manual, sale_discount: pm.sale_discount, kdv_rate: pm.kdv_rate } : l;
});

// Sunucudaki _apply_ops ile birebir aynı kurallar (istemci iyimser uygular, sunucu doğrular)
export const applyOp = (s, op) => {
  switch (op.op) {
    case "add": {
      const it = op.item;
      const idx = s.items.findIndex((l) => l.product_id === it.product_id);
      if (idx >= 0) {
        const cur = { ...s.items[idx], quantity: r2((Number(s.items[idx].quantity) || 0) + (Number(it.quantity) || 1)) };
        return { ...s, items: [cur, ...s.items.slice(0, idx), ...s.items.slice(idx + 1)] };
      }
      return { ...s, items: [it, ...s.items] };
    }
    case "set": return { ...s, items: s.items.map((l) => (l.product_id === op.pid ? { ...l, ...op.patch } : l)) };
    case "remove": return { ...s, items: s.items.filter((l) => l.product_id !== op.pid) };
    case "meta": {
      const p = op.patch || {};
      const n = { ...s };
      if ("title" in p) n.title = p.title;
      if ("note" in p) n.note = p.note;
      if ("payment_method" in p) n.payMethod = p.payment_method;
      if (p.mode) { n.mode = p.mode; n.items = applyModeToItems(n.items, p.mode); }
      return n;
    }
    case "clear": return { ...s, items: [] };
    default: return s;
  }
};

const fromServer = (doc, local) => ({
  ...local, cartId: doc.id, version: doc.version || 0, status: doc.status || "open", items: doc.items || [], mode: doc.mode || "genel",
  title: doc.title || "", note: doc.note || "", payMethod: doc.payment_method || "nakit",
  owner: doc.owner_name || "", ownerId: doc.owner_id || null, editors: doc.editors || [], updatedBy: doc.updated_by || "",
  adminPresent: !!doc.admin_present,   // yönetici sepette (sahip veya son 10 dk'da dokunan) → depo müdürü fiyatları göremez
});

export function usePosCart({ user, onClosedElsewhere }) {
  const [s, setS] = useState(() => { const saved = readLS(LS_CART, null); return saved && saved.state ? { ...EMPTY_CART, ...saved.state } : EMPTY_CART; });
  const [net, setNet] = useState("idle");                       // idle | live | syncing | offline
  const [pending, setPending] = useState(() => readLS(LS_PENDING, []));
  const sRef = useRef(s); sRef.current = s;
  const queue = useRef((readLS(LS_CART, null) || {}).queue || []);
  const flushing = useRef(false);
  const flushTimer = useRef(null);
  const retryTimer = useRef(null);
  const pendingBusy = useRef(false);
  const active = useRef(false);
  const self = useRef({});

  useEffect(() => { writeLS(LS_CART, { state: s, queue: queue.current }); }, [s]);

  const savePending = (list) => { setPending(list); writeLS(LS_PENDING, list); };
  const upsertPending = (p) => { const cur = readLS(LS_PENDING, []); const i = cur.findIndex((x) => x.key === p.key); if (i >= 0) cur[i] = p; else cur.push(p); savePending(cur); };
  const removePending = (key) => savePending(readLS(LS_PENDING, []).filter((x) => x.key !== key));

  const reset = () => { queue.current = []; setS(EMPTY_CART); };

  const handleClosed = (status, doc, err) => {
    queue.current = [];
    const cur = sRef.current;
    if (status === "missing") {
      if (cur.items.length) { setS((p) => ({ ...p, cartId: null, version: 0 })); queue.current.push({ op: "noop" }); self.current.scheduleFlush(); }
      else reset();
      return;
    }
    const who = doc?.completed_by || doc?.cancelled_by || "";
    const mine = doc?.completed_by_id && user && doc.completed_by_id === user.id;
    reset();
    if (!mine) {
      const msg = err ? apiError(err) : (status === "completed" ? `Sepet ${who ? who + " tarafından " : ""}tamamlandı — satış yapıldı.` : `Sepet ${who ? who + " tarafından " : ""}kapatıldı.`);
      toast.info(msg);
    }
    onClosedElsewhere && onClosedElsewhere(status, doc);
  };

  const lastSrv = useRef({ id: null, version: 0 });   // sunucudan gelen son sürüm (setState asenkron → sRef geride kalabilir)
  const adopt = (doc) => {
    if (!doc || doc.unchanged) return;
    if (doc.id) lastSrv.current = { id: doc.id, version: doc.version || 0 };
    if (doc.status && doc.status !== "open") { handleClosed(doc.status, doc); return; }
    setS((prev) => { let n = fromServer(doc, prev); for (const op of queue.current) n = applyOp(n, op); return n; });
  };

  const refetch = async () => {
    const cur = sRef.current;
    if (!cur.cartId || flushing.current) return;
    try {
      const { data } = await api.get(`/pos-carts/${cur.cartId}?since=${cur.version}&ap=${cur.adminPresent ? 1 : 0}`);
      adopt(data); setNet("live");
    } catch (e) {
      const cs = cartStatusOf(e);
      if (cs || e?.response?.status === 404) handleClosed(cs || "missing", e?.response?.data, e);
      else if (isTransient(e)) setNet("offline");
    }
  };

  const scheduleFlush = (ms = 180) => { if (flushTimer.current) clearTimeout(flushTimer.current); flushTimer.current = setTimeout(() => self.current.flush(), ms); };
  const scheduleRetry = () => { if (retryTimer.current) clearTimeout(retryTimer.current); retryTimer.current = setTimeout(() => { self.current.flush(); self.current.flushPending(); }, 6000); };

  const flush = async () => {
    if (flushing.current || !queue.current.length) return null;
    if (typeof navigator !== "undefined" && navigator.onLine === false) { setNet("offline"); scheduleRetry(); return null; }   // çevrimdışı: kuyruk cihazda bekler
    const cur = sRef.current;
    flushing.current = true; setNet("syncing");
    const ops = queue.current.splice(0).filter((o) => o.op !== "noop");
    if (cur.cartId && !ops.length) { flushing.current = false; refetch(); return null; }
    try {
      const { data } = cur.cartId
        ? await api.patch(`/pos-carts/${cur.cartId}/ops`, { ops })
        : await api.post("/pos-carts", { items: cur.items, mode: cur.mode, title: cur.title, note: cur.note, payment_method: cur.payMethod });
      adopt(data); setNet("live");
      return data;
    } catch (e) {
      const cs = cartStatusOf(e);
      if (cs || e?.response?.status === 404) handleClosed(cs || "missing", e?.response?.data, e);
      else if (isTransient(e)) { queue.current.unshift(...ops); setNet("offline"); scheduleRetry(); }
      else { toast.error(apiError(e)); await refetch(); }
      return null;
    } finally {
      flushing.current = false;
      if (queue.current.length) scheduleFlush();
    }
  };

  const dispatch = (op, ms) => { setS((prev) => applyOp(prev, op)); queue.current.push(op); scheduleFlush(ms); };

  // Ödeme: önce bekleyen op'lar akıtılır, sonra sepet tamamlanır. Bağlantı yoksa satış kuyruğa alınır (aynı anahtarla otomatik gönderim).
  const sendPending = async (p) => {
    const hdr = { headers: { "X-Idempotency-Key": p.key } };
    const body = { items: p.items, title: p.title, note: p.note, payment_method: p.payment_method, payments: p.payments || undefined, mode: p.mode, override_total: p.override_total, cash_received: p.cash_received || undefined, invoice_id: p.invoice_id || undefined };
    const { data } = p.cartId
      ? await api.post(`/pos-carts/${p.cartId}/complete`, { ...body, version: p.version }, hdr)
      : await api.post("/quick-sales", body, hdr);
    return data;
  };

  const complete = async ({ payMethod, overrideTotal, mapLine, payments = null, cashReceived = null, invoiceId = null }) => {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    // Bekleyen op'lar önce akıtılır (süren flush varsa beklenir); sunucunun döndürdüğü GÜNCEL sürüm kullanılır → yanlış 409 olmaz
    for (let i = 0; i < 100 && flushing.current; i++) await new Promise((r) => setTimeout(r, 30));
    let fresh = null;
    if (queue.current.length && net !== "offline" && !offline) { try { fresh = await flush(); } catch { /* aşağıda yerel kalemlerle devam */ } }
    const snap = sRef.current;
    const srv = fresh && fresh.id === snap.cartId ? fresh.version : (lastSrv.current.id === snap.cartId ? lastSrv.current.version : null);
    const version = srv != null ? Math.max(Number(srv) || 0, Number(snap.version) || 0) : snap.version;
    const items = snap.items.filter((l) => (Number(l.quantity) || 0) > 0).map(mapLine);
    if (!items.length) throw new Error("En az bir ürün ekleyin");
    const p = { key: uuid(), at: Date.now(), cartId: snap.cartId, version, title: (snap.title || "").trim(), mode: snap.mode, note: (snap.note || "").trim(),
      payment_method: payMethod, payments: payments && payments.length ? payments : null, override_total: overrideTotal, cash_received: cashReceived, invoice_id: invoiceId || undefined, items, rawItems: snap.items, owner: user?.name || "", grand: r2(items.reduce((a, l) => a + (Number(l.unit_gross) || 0) * (Number(l.quantity) || 0), 0)), status: "queued" };
    if (offline) { upsertPending(p); reset(); return { queued: true }; }   // tarayıcı çevrimdışı → beklemeden kuyruğa
    try {
      const sale = await sendPending(p);
      reset();
      return { ok: true, sale };
    } catch (e) {
      const cs = cartStatusOf(e);
      if (cs === "completed") { reset(); return { ok: true, elsewhere: true, message: apiError(e) }; }
      if (cs === "changed" || cs === "busy") { await refetch(); throw e; }
      if (isTransient(e)) { upsertPending(p); reset(); return { queued: true }; }
      throw e;
    }
  };

  const flushPending = async () => {
    if (pendingBusy.current || (typeof navigator !== "undefined" && navigator.onLine === false)) return;
    pendingBusy.current = true;
    try {
      for (const p of readLS(LS_PENDING, [])) {
        if (p.status === "error") continue;
        try {
          const sale = await sendPending(p);
          removePending(p.key);
          toast.success(`Bekleyen satış gönderildi · ${TRY(sale.grand_total)}`);
        } catch (e) {
          if (isTransient(e)) break;
          const cs = cartStatusOf(e);
          if (cs === "completed") { removePending(p.key); toast.info(apiError(e)); continue; }
          upsertPending({ ...p, status: "error", error: apiError(e) });
          toast.error(`Bekleyen satış gönderilemedi: ${apiError(e)}`);
        }
      }
    } finally { pendingBusy.current = false; }
  };

  const restorePending = (key) => {
    const p = readLS(LS_PENDING, []).find((x) => x.key === key);
    if (!p) return false;
    if (sRef.current.items.length) { toast.error("Önce açık sepetinizi tamamlayın veya kapatın"); return false; }
    removePending(key);
    queue.current = [{ op: "noop" }];
    setS({ ...EMPTY_CART, items: p.rawItems || [], mode: p.mode || "genel", title: p.title || "", note: p.note || "", payMethod: p.payment_method || "nakit" });
    scheduleFlush(50);
    return true;
  };

  // Sepet ekranı açıldı → sunucudaki güncel hali çek; kalem varsa "devam" (true)
  const begin = () => {
    active.current = true;
    if (sRef.current.cartId) refetch();
    if (queue.current.length) scheduleFlush(50);
    return sRef.current.items.length > 0 || !!sRef.current.cartId;
  };
  const park = () => { active.current = false; };
  const startFresh = () => { active.current = true; if (!sRef.current.items.length && !sRef.current.cartId) setS(EMPTY_CART); return begin(); };
  // Mevcut canlı sepeti sunucuda AÇIK bırakıp (Canlı Sepetler'de görünür) yeni boş bir sepet aç → aynı anda birden çok satış.
  const newBlank = async () => {
    if (queue.current.length) { try { await flush(); } catch { /* sunucuda açık kalır */ } }
    queue.current = [];
    active.current = true;
    setNet("idle");
    setS(EMPTY_CART);
  };

  const join = async (id) => {
    if (queue.current.length) await flush();
    const { data } = await api.get(`/pos-carts/${id}`);
    if (data.status && data.status !== "open") throw new Error("Bu sepet kapanmış");
    queue.current = [];
    setS(fromServer(data, EMPTY_CART));
    active.current = true;
    setNet("live");
    return data;
  };

  const cancelCart = async () => {
    const cur = sRef.current;
    active.current = false;
    if (cur.cartId) { try { await api.delete(`/pos-carts/${cur.cartId}`); } catch (e) { if (e?.response?.status === 403) { toast.error(apiError(e)); return false; } } }
    reset();
    return true;
  };
  const leave = () => { active.current = false; reset(); };

  self.current = { flush, scheduleFlush, flushPending };

  useCartChanges(() => { if (sRef.current.cartId) refetch(); });
  useEffect(() => {
    const up = () => { self.current.flush(); if (sRef.current.cartId) refetch(); self.current.flushPending(); };
    window.addEventListener("online", up);
    window.addEventListener("ge:server-up", up);
    const t = setTimeout(() => self.current.flushPending(), 1500);
    const iv = setInterval(() => { if (readLS(LS_PENDING, []).some((p) => p.status !== "error")) self.current.flushPending(); }, 20000);
    return () => { window.removeEventListener("online", up); window.removeEventListener("ge:server-up", up); clearTimeout(t); clearInterval(iv); if (flushTimer.current) clearTimeout(flushTimer.current); if (retryTimer.current) clearTimeout(retryTimer.current); };
  }, []); // eslint-disable-line

  const isOwner = !s.ownerId || (user && s.ownerId === user.id);
  return {
    items: s.items, mode: s.mode, title: s.title, note: s.note, payMethod: s.payMethod, cartId: s.cartId, version: s.version,
    owner: s.owner, ownerId: s.ownerId, editors: s.editors, updatedBy: s.updatedBy, isOwner, net, pending, adminPresent: !!s.adminPresent,
    dispatch, setMode: (m) => dispatch({ op: "meta", patch: { mode: m } }, 0), setTitle: (t, m) => dispatch({ op: "meta", patch: m ? { title: t, mode: m } : { title: t } }),
    setNote: (n) => dispatch({ op: "meta", patch: { note: n } }), setPayMethod: (p) => dispatch({ op: "meta", patch: { payment_method: p } }),
    begin, park, startFresh, newBlank, join, cancelCart, leave, complete, flush, flushPending, restorePending, removePending, refetch,
  };
}
